import Reflux from "reflux";
import _ from "lodash";
import AppConstants from "../constants/AppConstants";
import AssetActions from "../actions/AssetActions";
import UIActions from "../actions/UIActions";
import UploadManager from "../managers/UploadManager";
import FileUtils from "../utils/FileUtils";
import ErrorUtils from "../utils/ErrorUtils";
import { isValidLottieJson } from "../utils/LottieUtils";
import { queryClient } from "../utils/QueryUtils";
import { API_CONFIG_QUERY_KEY } from "../hooks/query/useApiConfig";
import { rejectedFilesList } from "../components/assetlibrary/RejectedFilesMessage";

var AssetUploadStore = Reflux.createStore({
  storeName: "AssetUploadStore",
  listenables: [AssetActions],

  init() {
    UploadManager.on("statusChange", this._onUploadStatusChange);
    UploadManager.on("asset", this._onUploadAsset);
    UploadManager.on("complete", this._onUploadAssetComplete);
    UploadManager.on("error", this._onUploadAssetError);
    //UploadManager.on('progress', this._onUploadAssetProgress);

    this.assets = [];

    this.assetMap = {};

    this.isUploading = false;
    this.numUploads = 0;
  },

  getList() {
    return this.assets;
  },

  getInfo() {
    return {
      numUploads: this.numUploads,
      assets: this.assets,
    };
  },

  onUploadFiles({ files, folder_id }) {
    if (!files || files.length === 0) return;

    const config = queryClient.getQueryData(API_CONFIG_QUERY_KEY);

    /**
     * @type {Array<{ file: File, fileInfo: ReturnType<typeof FileUtils.getSanitizedFileInfo> }>}
     */
    const valid = [];
    /**
     * @type {Array<{ file: File, reason: string }>}
     */
    const rejected = [];

    // first split files into `valid` and `rejected` buckets
    files.forEach((file) => {
      const fileInfo = FileUtils.getSanitizedFileInfo(file);

      // If config is missing, skip client-side validation; the API will enforce it
      const validation = config
        ? this._validateSanitizedFile(fileInfo, config)
        : { ok: true };

      if (validation.ok) {
        valid.push({ file, fileInfo });
      } else {
        rejected.push({ file, reason: validation.reason });
      }
    });

    if (rejected.length > 0) {
      this._showAlert({
        title: "Invalid Files Detected",
        reason: "Some files can't be uploaded",
        message: rejectedFilesList(rejected),
      });
    }

    if (valid.length === 0) {
      UIActions.hideImageUploadOverlay();
      return;
    }

    valid.forEach(({ file, fileInfo }) => {
      this._processFile(file, fileInfo, folder_id);
    });
    this.trigger();
  },

  /**
   * Returns an object indicating whether the file is valid, and if not, the reason why.
   * @param {ReturnType<typeof FileUtils.getSanitizedFileInfo>} fileInfo
   * @param {{ allowedFileTypes: string[], maxFileSize: number, maxFileSizeGif?: number, maxFileSizeLottie?: number }} config
   * @returns {{ ok: boolean, reason?: string }}
   */
  _validateSanitizedFile(fileInfo, config) {
    if (!fileInfo.ext || !config.allowedFileTypes.includes(fileInfo.ext)) {
      return { ok: false, reason: "Unsupported file type" };
    }

    const fileSizeLimits = {
      [AppConstants.EXT_GIF]: config.maxFileSizeGif,
      [AppConstants.EXT_LOTTIE]: config.maxFileSizeLottie,
      [AppConstants.EXT_JSON]: config.maxFileSizeLottie,
    };

    const maxSize = fileSizeLimits[fileInfo.ext] ?? config.maxFileSize;

    if (fileInfo.size > maxSize) {
      const limitMb = Math.floor(FileUtils.bytesToMb(maxSize));
      return {
        ok: false,
        reason: `File is too large (exceeds ${limitMb} MB limit)`,
      };
    }
    return { ok: true };
  },

  _processFile(file, fileInfo, folder_id) {
    const fileURL = window.URL.createObjectURL(file);
    const id = fileURL.replace(/(\.|%|:|\/)/g, "");

    const asset = {
      id,
      name: fileInfo.originalName,
      filename: fileInfo.filename,
      ext: fileInfo.ext,
      type: FileUtils.getAssetType(fileInfo.ext),
      size: file.size,
      status: AppConstants.UPLOAD_STATUS_PENDING,
      progress: 0,
      file,
      folder_id,
      file_url: fileURL,
    };

    this.numUploads++;
    this._createHtmlElement(asset);
  },

  _createHtmlElement(asset) {
    const { type, ext } = asset;
    if (type === AppConstants.ASSET_TYPE_IMAGE)
      return this._createImgElement(asset);
    if (type === AppConstants.ASSET_TYPE_VIDEO)
      return this._createVideoElement(asset);
    if (ext === AppConstants.EXT_JSON)
      return this._validateJsonAndUpload(asset);
    return this._addAssetToUpload(asset);
  },

  _validateJsonAndUpload(asset) {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        const result = isValidLottieJson(json);
        if (result.success) {
          this._addAssetToUpload(asset);
          return;
        }
      } catch {
        // JSON parse failure falls through to rejection below
      }
      this.numUploads = Math.max(0, this.numUploads - 1);
      UIActions.showToast({
        message: "This JSON file is not a valid Lottie animation",
        status: "warning",
        hasClose: true,
        dur: 0,
      });
      UIActions.hideImageUploadOverlay();
      this.trigger();
    };

    reader.readAsText(asset.file);
  },

  _showAlert({ title = "File Too Large", reason, message }) {
    UIActions.modalOpen(AppConstants.MODAL_ACTION_CONFIRM, {
      title,
      question: reason,
      message: message,
      confirmBtn: "Ok",
      alertMode: true,
    });
  },

  _createImgElement(asset) {
    var img = new Image();
    var that = this;
    img.src = asset.file_url;
    img.onload = function () {
      this.onload = null;
      this.onerror = null;
      asset.height = this.height;
      asset.width = this.width;
      asset.aspect_ratio = +(this.width / this.height).toFixed(5);
      asset.placeholder = asset.file_url;

      that._addAssetToUpload(asset);
    };
    img.onerror = function () {
      this.onload = null;
      this.onerror = null;

      that.numUploads = Math.max(0, that.numUploads - 1);
      UIActions.showToast({
        message: `Unable to load "${asset.name}." The file may be corrupt.`,
        status: "warning",
        hasClose: true,
        dur: 0,
      });
      UIActions.hideImageUploadOverlay();
      that.trigger();
    };
  },

  _createVideoElement(asset) {
    var video = document.createElement("video");
    var that = this;
    video.src = asset.file_url;

    video.onerror = function (err) {
      console.log("Video Invalid", err);
      alert(
        "Unable to upload\n\n" +
          asset.name +
          "\n\nVideo file is not valid.\nPlease verify encoding."
      );
      this.oncanplay = null;
      this.onerror = null;

      that.numUploads--;
      that.trigger();
    };

    video.oncanplay = function () {
      this.oncanplay = null;
      this.onerror = null;

      asset.height = this.videoHeight;
      asset.width = this.videoWidth;
      asset.aspect_ratio = +(asset.width / asset.height).toFixed(5);

      var canvas = document.createElement("canvas");
      canvas.width = this.videoWidth;
      canvas.height = this.videoHeight;
      var ctx = canvas.getContext("2d");
      ctx.drawImage(this, 0, 0, this.videoWidth, this.videoHeight);
      var dataURL = canvas.toDataURL();
      asset.placeholder = dataURL;

      that._addAssetToUpload(asset);
    };
  },

  _addAssetToUpload(asset) {
    _.set(this.assetMap, asset.id, asset);

    UploadManager.uploadAssetFile(asset, asset.file);

    delete asset.file; //don't need the file on the asset object once it is passed to the upload manager
  },

  _onUploadStatusChange(isUploading) {
    this.isUploading = isUploading;

    this.trigger();
  },

  _onUploadAsset(data) {
    var serverAsset = data.asset;

    var asset = _.get(this.assetMap, data.id);
    if (asset) {
      asset.status = AppConstants.UPLOAD_STATUS_IN_PROGRESS;
      asset.asset_id = serverAsset.asset_id;
      asset.key = serverAsset.key;
      asset.asset_type = serverAsset.asset_type;
      asset.asset_name = serverAsset.asset_name;
      asset.aspect_ratio = serverAsset.aspect_ratio;
      asset.ext = serverAsset.ext;
      asset.folder_id = serverAsset.folder_id;

      this.assets.push(asset);
      this.trigger();
    }
  },

  /**
   * @param {{ id: string, error?: boolean, completeData?: import('../service/API').CompleteUploadResponse }} data
   */
  _onUploadAssetComplete(data) {
    this.numUploads = Math.max(0, this.numUploads - 1);
    var asset = _.get(this.assetMap, data.id);

    if (asset) {
      var hasError = _.get(data, "error", false);

      if (hasError) {
        asset.status = AppConstants.ASSET_STATUS_UNAVAILABLE;
      } else {
        asset.status = AppConstants.ASSET_STATUS_ACTIVE;
      }

      const { completeData } = data;
      if (completeData && completeData.asset_type !== asset.asset_type) {
        asset.asset_type = completeData.asset_type;
      }

      _.remove(this.assets, function (cAsset) {
        return cAsset.id === asset.id;
      });

      _.unset(this.assetMap, data.id);

      AssetActions.completeAssetUpload(asset); //this will trigger a change
    }

    this.trigger();
  },

  _onUploadAssetError(err) {
    this.numUploads = Math.max(0, this.numUploads - 1);

    var asset = _.get(this.assetMap, err.id);
    if (asset) {
      var message = "Upload Error";
      if (_.has(err.error, "error")) {
        const error = ErrorUtils.getFormattedError(err.error);
        message = error.message;
      }
      asset.status = AppConstants.UPLOAD_STATUS_ERROR;
      asset.message = message;
      asset.asset_name = asset.name;
      asset.asset_id = err.id;
      asset.ext = "";

      _.unset(this.assetMap, asset.id);

      AssetActions.completeAssetUpload(asset); //this will trigger a change
    }

    this.trigger();
  },

  _onUploadAssetProgress(data) {
    var asset = _.get(this.assetMap, data.id);
    if (asset) {
      asset.progress = Math.round(data.percent);
      this.trigger();
    }
  },

  _removeUploadingAsset(asset) {
    window.URL.revokeObjectURL(asset.file_url);
  },
});

export default AssetUploadStore;
